import { useEffect, useRef, useState } from 'react';
import type { Inspection, LiveInspectionEvent } from '@visionqc/shared';

interface LiveState {
  connected: boolean;
  last: Inspection | null;
  recent: Inspection[];
}

/**
 * Subscribes to /ws/live and tracks the latest inspection plus a rolling window
 * of recent ones (for throughput). Auto-reconnects with a short backoff.
 */
export function useLive(windowMs = 60_000): LiveState {
  const [connected, setConnected] = useState(false);
  const [last, setLast] = useState<Inspection | null>(null);
  const [recent, setRecent] = useState<Inspection[]>([]);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    let closed = false;
    let retry: ReturnType<typeof setTimeout>;

    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${window.location.host}/ws/live`);
      socketRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!closed) retry = setTimeout(connect, 1500);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data) as LiveInspectionEvent | { type: string };
          if (msg.type === 'inspection') {
            const insp = (msg as LiveInspectionEvent).inspection;
            setLast(insp);
            setRecent((prev) => {
              const cutoff = Date.now() - windowMs;
              return [insp, ...prev].filter((i) => new Date(i.time).getTime() >= cutoff);
            });
          }
        } catch {
          /* ignore malformed frames */
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      socketRef.current?.close();
    };
  }, [windowMs]);

  return { connected, last, recent };
}
